import {
	FOLDER_NAME,
	FOLDER_NAME_MAX,
	CLAIM_NAME_MAX,
} from '@/components/dialogs/AddFolderSharepointDialog/validationSchema';
import { useGetClientRowQuery } from '@/features/clients/clientsApi';
import { useGetFoldersAndFilesQuery } from '@/features/msGraph/msGraphApi';
import { getFolderFromPipeline, getPipelineFromSlug, tryDecodeFilePath } from '@/utils/constants';
import {
	CircularProgress,
	FormHelperText,
	FormLabel,
	InputAdornment,
	Stack,
	TextField,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { paths } from '@/app/routes';
import { FOLDER_NAME_HELP_TEXT } from '@/utils/helperText';
import { useGetDealsByClientIdQuery } from '@/features/deals/dealsApi';
import { useFormikContext } from 'formik';

const arraysEqual = (a = [], b = []) =>
	a.length === b.length && a.every((val, index) => val === b[index]);

const FolderNameField = ({
	// values,
	disabled,
	dealName = 'Deal name',
	// errors,
	// setFieldValue,
	// getFieldProps,
	// touched,
	...formik
}) => {
	const hasFormik = Object.keys(formik).length > 0;

	const { values, errors, setFieldValue, getFieldProps, touched } = hasFormik
		? formik
		: useFormikContext();

	const location = useLocation();
	const pipelineId = getPipelineFromSlug(location.pathname);
	const dealParentFolderName = getFolderFromPipeline(pipelineId);

	// Check if this is a claim
	const isClaims = location.pathname.includes(paths.createClaim);
	const maxLength = isClaims ? CLAIM_NAME_MAX : FOLDER_NAME_MAX;

	const hasError = touched?.[FOLDER_NAME] && Boolean(errors?.[FOLDER_NAME]);

	const [name, setName] = useState(values?.[FOLDER_NAME] ?? '');
	useEffect(() => setName(values?.[FOLDER_NAME] ?? ''), [values?.[FOLDER_NAME]]);

	const { data: clientRow, ...clientRowQuery } = useGetClientRowQuery(values.clientId, {
		skip: !values.clientId,
	});

	const clientDealsQuery = useGetDealsByClientIdQuery(values.clientId, {
		skip: !values.clientId,
	});
	const clientDeals = clientDealsQuery.currentData ?? [];

	const clientDealsByYearFolder = useMemo(() => {
		const data = clientDeals.filter(
			(d) =>
				values.yearFolder && d.yearFolder == values.yearFolder && d.pipeline === pipelineId,
		);
		// console.log(
		// 	`Client '${pipelineId}' (${dealParentFolderName}) deals by year folder ${values.yearFolder}`,
		// 	data,
		// );
		return data.map((d) => d.dealName.trim());
	}, [values.yearFolder, clientDeals, pipelineId]);

	// The first level of folders in the client folder (e.g. inside client name folder)
	const clientFoldersQuery = useGetFoldersAndFilesQuery(
		{
			itemId: clientRow?.driveId,
			driveId: clientRow?.clientFolderDriveId,
			recursive: false,
		},
		{
			skip: !clientRow?.driveId || !clientRow?.clientFolderDriveId,
		},
	);

	// The drive id of the year folder (e.g. '25-26')
	const yearPathDriveId = useMemo(() => {
		const { folders } = clientFoldersQuery.currentData ?? { folders: [] };
		const folder = folders.find((f) => tryDecodeFilePath(f.name, values.yearFolder));
		// console.log('Year path folder', { folder });
		return folder?.id;
	}, [clientFoldersQuery.currentData, values.yearFolder]);

	// The first level of folders inside the year folder (e.g. '25-26')
	const yearPathFilesQuery = useGetFoldersAndFilesQuery(
		{
			itemId: yearPathDriveId,
			driveId: clientRow?.clientFolderDriveId,
			recursive: false,
		},
		{
			skip: !yearPathDriveId || !clientRow?.clientFolderDriveId,
		},
	);

	// The drive id of the deal parent folder (e.g. 'Cover Information' for New Business deals)
	const dealParentFolderDriveId = useMemo(() => {
		const { folders } = yearPathFilesQuery.currentData ?? { folders: [] };
		const folder = folders.find((f) => tryDecodeFilePath(f.name, dealParentFolderName));
		return folder?.id;
	}, [yearPathFilesQuery.currentData, dealParentFolderName]);

	// The first level of folders inside the deal parent folder (e.g. 'Cover Information' for New Business deals)
	const dealFolderFilesQuery = useGetFoldersAndFilesQuery(
		{
			itemId: dealParentFolderDriveId,
			driveId: clientRow?.clientFolderDriveId,
			recursive: false,
		},
		{
			skip: !dealParentFolderDriveId || !clientRow?.clientFolderDriveId,
		},
	);

	const existingFolders = useMemo(() => {
		const { folders } = dealFolderFilesQuery.currentData ?? { folders: [] };
		// console.log(`The deal folders inside ${dealParentFolderName}`, folders);
		return folders.map((f) => f.name);
	}, [dealFolderFilesQuery.currentData]);

	const existingFoldersAndDealNames = useMemo(
		() => Array.from(new Set(existingFolders.concat(clientDealsByYearFolder))),
		[existingFolders, clientDealsByYearFolder],
	);

	useEffect(() => {
		const current = values.existingFolders ?? [];
		const next = existingFoldersAndDealNames ?? [];

		// console.log('Arrays equal', { current, next, equal: arraysEqual(current, next) });
		if (!arraysEqual(current, next)) {
			setFieldValue('existingFolders', existingFoldersAndDealNames);
		}
	}, [values.existingFolders, existingFoldersAndDealNames]);

	useEffect(() => {
		if (!values[FOLDER_NAME]) return;
		setTimeout(() => {
			formik?.validateField?.(FOLDER_NAME);
		}, 100);
	}, [values.existingFolders, values[FOLDER_NAME], values.clientId, values.yearFolder]);

	const onBlur = (e) => {
		setFieldValue(FOLDER_NAME, name.trim());
		setName((prev) => prev.trim());

		setTimeout(() => {
			getFieldProps(FOLDER_NAME).onBlur?.(e);
			if (isClaims && !values?.describeClaim && name.trim()) {
				setFieldValue('describeClaim', name.trim());
			}
		}, 100);
	};

	const loading =
		dealFolderFilesQuery.isLoading ||
		yearPathFilesQuery.isLoading ||
		clientFoldersQuery.isLoading ||
		clientDealsQuery.isLoading ||
		clientRowQuery.isLoading;

	return (
		<>
			<FormLabel htmlFor={FOLDER_NAME} required error={hasError}>
				{dealName}
			</FormLabel>
			<TextField
				fullWidth
				name={FOLDER_NAME}
				id={FOLDER_NAME}
				{...getFieldProps(FOLDER_NAME)}
				value={name}
				onChange={(e) => setName(e.target.value.slice(0, maxLength))}
				disabled={disabled || loading}
				error={hasError}
				InputProps={{
					...(loading && {
						endAdornment: (
							<InputAdornment position='end'>
								<CircularProgress size='1em' />
							</InputAdornment>
						),
					}),
				}}
				required
				onBlur={onBlur}
			/>

			<Stack direction='row' justifyContent='space-between'>
				<Stack direction='column'>
					{hasError && <FormHelperText error>{errors[FOLDER_NAME]}</FormHelperText>}

					<FormHelperText>{FOLDER_NAME_HELP_TEXT}</FormHelperText>
				</Stack>

				<FormHelperText
					error={hasError || name.length > maxLength}
					sx={{
						...(name.length >= maxLength && {
							fontWeight: 'bold',
						}),
						mr: '14px',
					}}
				>
					<span>{name.length}</span>
					{`/${maxLength}`}
				</FormHelperText>
			</Stack>
		</>
	);
};

export default FolderNameField;
